<template>
  <div
    class="canvasTable"
    tabindex="0"
    @keydown.up.prevent="moveSelection(-1)"
    @keydown.down.prevent="moveSelection(1)"
  >
    <div class="tableWrap" ref="tableWrapEl">
      <table>
        <thead>
          <tr>
            <th
              v-for="col in visibleColumns"
              :key="col"
              @click="toggleSort(col)"
              class="sortableHeader"
            >
              {{ col }}
              <span v-if="sortCol === col" class="sortArrow">{{ sortDir === 'asc' ? '\u25B2' : '\u25BC' }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, i) in sortedRows"
            :key="i"
            :ref="(el) => { if (el) rowEls[i] = el as HTMLElement }"
            :class="{ selected: selectedIndex === i, clickable: isClickable }"
            @click="onRowClick(row, i)"
          >
            <td v-for="col in visibleColumns" :key="col">{{ formatCell(row[col]) }}</td>
          </tr>
          <tr v-if="sortedRows.length === 0">
            <td :colspan="visibleColumns.length" class="emptyRow">No data</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="rows.length > 0" class="tableFooter">
      {{ rows.length }} row{{ rows.length === 1 ? '' : 's' }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { useContextDrawer } from '../../drawer/useContextDrawer'
import {
  DRAWER_CONTEXT_ACCOUNT,
  DRAWER_CONTEXT_CONTACT,
  DRAWER_CONTEXT_REACHOUT,
} from '../../drawer/drawerTypes'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const props = defineProps<{
  columns: string[]
  rows: Record<string, any>[]
  rowEntity?: string | null
}>()

const { setContext: setDrawerContext } = useContextDrawer()

const sortCol = ref<string | null>(null)
const sortDir = ref<'asc' | 'desc'>('asc')
const selectedIndex = ref<number | null>(null)
const tableWrapEl = ref<HTMLElement | null>(null)
const rowEls = ref<Record<number, HTMLElement>>({})

// Hide columns that are pure UUID IDs — they're for linking, not display.
const visibleColumns = computed(() => {
  if (!props.rows.length) return props.columns
  return props.columns.filter(col => {
    if (col === 'tenant_id') return false
    const values = props.rows.map(r => r[col]).filter(v => v != null)
    if (values.length === 0) return true
    return !values.every(v => typeof v === 'string' && UUID_RE.test(v))
  })
})

const isClickable = computed(() => !!props.rowEntity)

function onRowClick(row: Record<string, any>, index: number) {
  selectedIndex.value = index
  openDrawerForRow(row)
}

function moveSelection(delta: number) {
  if (!sortedRows.value.length) return
  let next: number
  if (selectedIndex.value === null) {
    next = delta > 0 ? 0 : sortedRows.value.length - 1
  } else {
    next = selectedIndex.value + delta
  }
  next = Math.max(0, Math.min(next, sortedRows.value.length - 1))
  selectedIndex.value = next

  // Auto-scroll the selected row into view.
  nextTick(() => {
    const el = rowEls.value[next]
    if (el) {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  })

  // Open drawer for the newly selected row.
  openDrawerForRow(sortedRows.value[next])
}

function openDrawerForRow(row: Record<string, any>) {
  if (!props.rowEntity) return

  if (props.rowEntity === 'contact') {
    const contactId = String(row.contact_id || row.id || '')
    const accountId = String(row.account_id || '')
    if (contactId && accountId) {
      setDrawerContext({ type: DRAWER_CONTEXT_CONTACT, contactId, accountId })
    }
  } else if (props.rowEntity === 'reachout') {
    const reachoutId = String(row.reachout_id || row.id || '')
    const accountId = row.account_id ? String(row.account_id) : undefined
    const contactId = row.contact_id ? String(row.contact_id) : undefined
    if (reachoutId) {
      setDrawerContext({ type: DRAWER_CONTEXT_REACHOUT, reachoutId, accountId, contactId })
    }
  } else if (props.rowEntity === 'account') {
    const accountId = String(row.account_id || row.id || '')
    if (accountId) {
      setDrawerContext({ type: DRAWER_CONTEXT_ACCOUNT, accountId })
    }
  }
}

function toggleSort(col: string) {
  if (sortCol.value === col) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortCol.value = col
    sortDir.value = 'asc'
  }
}

const sortedRows = computed(() => {
  if (!sortCol.value) return props.rows
  const col = sortCol.value
  const dir = sortDir.value === 'asc' ? 1 : -1
  return [...props.rows].sort((a, b) => {
    const va = a[col]
    const vb = b[col]
    if (va == null && vb == null) return 0
    if (va == null) return 1
    if (vb == null) return -1
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
    return String(va).localeCompare(String(vb)) * dir
  })
})

function formatCell(value: any): string {
  if (value == null) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}
</script>

<style scoped>
.canvasTable {
  outline: none;
}
.tableWrap {
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
}
table {
  min-width: 100%;
  width: max-content;
  border-collapse: collapse;
  font-size: 13px;
}
th, td {
  text-align: left;
  padding: 6px 10px;
  border-bottom: 1px solid var(--color-border-subtle, var(--border, #e5e7eb));
  white-space: nowrap;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
}
th:first-child, td:first-child { padding-left: 16px; }
th:last-child, td:last-child { padding-right: 16px; }
th {
  position: sticky;
  top: 0;
  background: var(--color-bg-surface, var(--surface, #fff));
  font-weight: 600;
  color: var(--color-text-secondary, var(--muted, #6b7280));
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 1;
}
.sortableHeader {
  cursor: pointer;
  user-select: none;
}
.sortableHeader:hover {
  color: var(--color-text-primary, var(--text, #1a1a1a));
}
.sortArrow {
  font-size: 9px;
  margin-left: 3px;
}
tbody tr:hover:not(.selected) td {
  background: var(--color-bg-subtle, #F2EDE5);
}
tbody tr.selected td {
  background: var(--color-bg-sunk, #ECE7DE);
}
/* When any row is selected, dim hover on other rows */
tbody:has(.selected) tr:hover:not(.selected) td {
  background: var(--color-bg-subtle, #F2EDE5);
  opacity: 0.6;
}
tbody tr.clickable {
  cursor: pointer;
}
tbody tr:not(.clickable) {
  cursor: default;
}
.emptyRow {
  text-align: center;
  color: var(--color-text-secondary, var(--muted, #6b7280));
  padding: 24px;
}
.tableFooter {
  padding: 6px 10px;
  font-size: 11px;
  color: var(--color-text-secondary, var(--muted, #6b7280));
  border-top: 1px solid var(--color-border-subtle, var(--border, #e5e7eb));
}
</style>
